import '../scss/components/aps-document.scss';
import {initSnapSlider} from './utils/snap-slider.js';
import {dlv} from './utils/dlv.js';
import { formatUrl } from './utils/urlFormatter.js';
import {ensureArray} from './utils/ensure-array.js';
import {serializeRF} from "./utils/parseSerializeRF.js";
import {setAlert} from "./utils/alert.js";
import {initLightbox, initLightboxActions} from "./utils/lightbox.js";
import {getUrl} from "./utils/router.js";

const keyMapping = {
    geografischer_bezug: 'Geografischer Bezug',
    stichworte: 'Stichworte',
    docNumber: 'Dokumentnummer',
    archive: 'Sammlung',
    sammlung: 'Sammlung',
    kr_ordnung: 'KR-Ordnung',
    copyright: 'Copyright',
    publikation: 'Publikation',
    signatur: 'Signatur',
    objektart: 'Objektart',
    bildagentur: 'Bildagentur',
    fotograf_in: 'FotografIn',
    kuenstler_in: 'KünstlerIn',
    datierung: 'Datierung',
    masse: 'Maße',
    aufbewahrungsort: 'Aufbewahrungsort',
};

const typeMap = {
    personen: {
        facet: 'ra_facet_personen',
        IconComp: '<path style="fill:currentColor" d="M12 12a4 4 0 100-8 4 4 0 000 8zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>',
    },
    sache_thema: {
        facet: 'ra_facet_thema',
        IconComp: '<path style="fill:currentColor" d="M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z" />',
    },
    ort: {
        facet: 'ra_facet_ort',
        IconComp: '<path style="fillcurrentColor" d="M12 2a7 7 0 00-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 00-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z"/>',
    },
    kuenstler_in_architekt_in: {
        facet: null,
        IconComp: '<path style="fill:currentColor" d="M12 3a9 9 0 000 18 1.5 1.5 0 001.11-2.51A1.5 1.5 0 0114.23 16H16a5 5 0 005-5c0-4.42-4.03-8-9-8zm-5.5 9a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm3-4a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm5 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm3 4a1.5 1.5 0 110-3 1.5 1.5 0 010 3z"/>',
    },
};

const docEl = document.querySelector('.aps-document');
let noteLength;
let documentParams;
let doc;

if (docEl) {
    const dataNumber = docEl.dataset.number;
    const [dataType] = dataNumber.split('D');
    const dataAttachment = docEl.dataset.attachment;
    documentParams = { dataType, dataNumber, dataAttachment}

    const detailResponse = await fetchApsData(`/api/aps/document/${dataType}/${dataNumber}`);
    doc = dlv(detailResponse, dataType);
    const attachments = dlv(doc, 'attachments.attachment');

    if(doc) {
        document.title = `${dlv(doc, '_visible.titel') || dataAttachment} | Greven Archiv Digital`;

        let loginState = getLoginState();

        updateTopBarNavigation(documentParams);

        insertDocumentImage(attachments, documentParams);

        insertDocumentHead(doc);

        initDocumentOrderForm();

        insertCurrentAttachments(attachments, documentParams);

        insertDocumentDetails(doc, documentParams);

        // await initSimiliarImages(documentParams, dataAttachment);

        if(loginState) {
            await initDocumentNotes(documentParams);
            document.querySelector('.aps-document_notices button').addEventListener('click', async (event) => {
                event.preventDefault();
                const note = document.querySelector('.aps-document_notices textarea').value;
                let fetchMethod = note.length === 0 ? 'DELETE' : noteLength === 0 ? 'POST' : 'PATCH';

                if(fetchMethod === 'DELETE') {
                    await fetch(`/api/note/${dataNumber}/${dataAttachment}`, {
                        method: fetchMethod,
                        credentials: 'include'
                    }).then(response => {
                        setAlert('success', 'Ihre Notiz wurde gelöscht.');
                    }).catch(error => {
                        setAlert('error','Tut uns leid, aber Ihre Notiz konnte nicht gelöscht werden.');
                    });
                } else if(fetchMethod === 'POST' || fetchMethod === 'PATCH') {
                    await fetch(`/api/note`, {
                        method: fetchMethod,
                        credentials: 'include',
                        body: JSON.stringify({
                            message: note,
                            apsImageId: dataNumber,
                            attachment: dataAttachment
                        })
                    }).then(response => {
                        setAlert('success', 'Ihre Notiz wurde gespeichert. Vielen Dank!');
                    }).catch(error => {
                        setAlert('error','Tut uns leid, aber Ihre Notiz konnte nicht gespeichert werden.');
                    });
                }
            });
        } else {
            const noteEl = docEl.querySelector('.aps-document_notices');
            if(noteEl) noteEl.remove();
        }
    }

    initLightbox().then(() => {
        const addButton = docEl.querySelector('.aps-document_main .button[data-lightbox="add"]');
        const removeButton = docEl.querySelector('.aps-document_main .button[data-lightbox="remove"]');

        initLightboxActions(
            { addEl: addButton, removeEl: removeButton },
            {
                dataNumber: dataNumber,
                dataAttachment: dataAttachment,
                archive: dlv(doc, '_visible.objektinformationen.sammlung', ''),
                creator: dlv(doc, '_visible.objektinformationen.fotograf_in', ''),
                title: dlv(doc, '_visible.titel', 'Ohne Titel'),
                thumbnail: dlv(doc, '_visible.thumbnail.url', ''),
            }
        );
    });
}

async function fetchApsData(url, method = 'GET', data) {
    try {
        let response;

        if(data == null) {
            response = await fetch(url, {method: method, credentials: 'include'})
        } else {
            response = await fetch(url, {
                method: method,
                credentials: 'include',
                body: JSON.stringify(data)
            })
        }
        return await response.json();
    } catch (error) {
        //console.error("Error:", error);
        return {error: true, message: error.message};
    }
}

function getLoginState() {
    return window.isLoggedIn;
}
function updateTopBarNavigation(documentParams) {
    const navEl = docEl.querySelector('.aps-document_nav');
    const lsApsSearch = localStorage.getItem('aps_search');
    if (lsApsSearch == null) {
        navEl.remove();
    } else {
        const parsedLsApsSearch = JSON.parse(lsApsSearch);
        const backUrl = parsedLsApsSearch.url;
        const items = parsedLsApsSearch.items;

        const curItemIndex = items.findIndex(item => item.attachmentId === documentParams.dataAttachment && item.docNumber === documentParams.dataNumber);
        if(curItemIndex !== -1) {
            const prevItem = curItemIndex - 1 < 0 ? null : items[curItemIndex - 1];
            const nextItem = curItemIndex + 1 > items.length ? null : items[curItemIndex + 1];
            const prevItemUrl = prevItem ? prevItem.url : null;
            const nextItemUrl = nextItem ? nextItem.url  : null;

            const allBtnEl = navEl.querySelector('.document-nav_action--back');
            const prevBtnEl = navEl.querySelector('.document-nav_action--prev');
            const nextBtnEl = navEl.querySelector('.document-nav_action--next');

            if (!!backUrl) {
                allBtnEl.style.display = 'flex';
                allBtnEl.href = backUrl;
            } else {
                allBtnEl.style.display = 'none';
            }
            if (!!prevItemUrl) {
                prevBtnEl.style.display = 'flex';
                prevBtnEl.href = prevItemUrl;
            } else {
                prevBtnEl.style.display = 'none';
            }
            if (!!nextItemUrl) {
                nextBtnEl.style.display = 'flex';
                nextBtnEl.href = nextItemUrl;
            } else {
                nextBtnEl.style.display = 'none';
            }
        } else {
            navEl.remove();
        }
    }
    navEl.classList.remove('d-none')
}
function insertDocumentImage(data, params) {
    const tileEl = docEl.querySelector('.aps-document_tile');
    const arrowPrevEl = tileEl.querySelector('.aps-document_tile_arrow-prev');
    const arrowNextEl = tileEl.querySelector('.aps-document_tile_arrow-next');
    if(data && Array.isArray(data)) {
        const curAttachmentIndex = data.findIndex(item => item.id === params.dataAttachment);

        if(curAttachmentIndex === -1) {
            arrowPrevEl.remove();
            arrowNextEl.remove();
        } else if(curAttachmentIndex === 0) {
            arrowNextEl.href = `/dokument/${params.dataNumber}/${data[curAttachmentIndex + 1].id}`;
            arrowNextEl.title = `Nächstes Dokument: ${data[curAttachmentIndex + 1].id}`;
            arrowPrevEl.remove();
        } else if (curAttachmentIndex > 0) {
            arrowPrevEl.href = `/dokument/${params.dataNumber}/${data[curAttachmentIndex - 1].id}`;
            arrowPrevEl.title = `Vorheriges Dokument: ${data[curAttachmentIndex - 1].id}`;
            arrowNextEl.remove();
        }
    } else {
        arrowPrevEl.remove();
        arrowNextEl.remove();
    }

    tileEl.classList.add('loaded');
}
function insertDocumentHead(data) {
    const headline = docEl.querySelector('.aps-document_main-headline h1');
    const headlineText = docEl.querySelector('.aps-document_main-headline p');
    const headlineDesc = docEl.querySelector('.aps-document_main-desc');
    const heading =  dlv(data['_visible'], 'titel', 'Ohne Titel');
    const creator = dlv(data['_visible'], 'objektinformationen.fotograf_in');
    const date = dlv(data['_visible'], 'objektinformationen.datierung');
    const description = dlv(data['_visible'], 'inhaltsinformationen.beschreibung');

    let paragraph = '';
    if (creator) {
        paragraph += `<span>${creator}</span>`;
    }
    if (creator && date) {
        paragraph += ' - ';
    }
    if (date) {
        paragraph += `<span>${date}</span>`;
    }

    if (heading && heading.length > 0) headline.innerHTML = heading;
    if (paragraph && paragraph.length > 0) headlineText.innerHTML = paragraph;
    if (description && description.length > 0) headlineDesc.innerHTML = description;
}
function insertCurrentAttachments(data, params) {
    const attachmentEl = docEl.querySelector('.aps-document_attachments');
    const snapSliderEl = attachmentEl.querySelector('.topic-carousel');

    if (data && Array.isArray(data)) {
        const carouselLoadingEl = attachmentEl.querySelector('.sk-load-box-row');
        if (carouselLoadingEl) carouselLoadingEl.remove();

        insertAttachments(attachmentEl.querySelector('.scroll-snap-slider'), data, params, true);
        snapSliderEl.classList.add('loaded');
    } else if(data && typeof data === "object") {
        attachmentEl.remove();
    }
}
function insertDocumentDetails(doc, params) {
    const detailsEl = docEl.querySelector('.aps-document_details-image');
    const objectEl = docEl.querySelector('.aps-document_details-object');
    const additionalEl = docEl.querySelector('.aps-document_details-additional');

    const geografischer_bezug = dlv(doc, '_visible.inhaltsinformationen.geografischer_bezug');
    const archive = dlv(doc, '_visible.sammlung');
    const objektinformationen = dlv(doc, '_visible.objektinformationen');
    const zusatzinformationen = dlv(doc, '_visible.zusatzinformationen');
    const copyright = dlv(zusatzinformationen, 'copyright');
    const publikation = dlv(zusatzinformationen, 'publikation');

    /*const schlagwort = dlv(data, 'meta.schlagwort');
    const googleVision = dlv(data, 'googleVision.gold');*/
    const stichworte = {
        ...dlv(doc, '_visible.inhaltsinformationen.stichworte', {}),
        /*...(schlagwort ? { schlagwort } : {}),
        ...(googleVision ? { googleVision } : {}),*/
    };
    const hasStichworte = Boolean(stichworte != null && Object.keys(stichworte).length > 0);

    let tags = [];
    let kr_ordnung = dlv(doc, '_visible.zusatzinformationen.kr_ordnung');

    if (hasStichworte) {
        Object.keys(stichworte).forEach(key => {
            ensureArray(stichworte[key]).forEach(tag => {
                if (!tags.some(t => t.value === tag)) {
                    tags.push({type: key, value: tag});
                }
            });
        });
    }
    if (kr_ordnung != null) {
        let krStr = '';
        const processKrOrdnung = obj => {
            if (typeof obj === 'string') {
                if (krStr.length === 0) {
                    krStr = krStr + obj;
                } else {
                    krStr = krStr + ' > ' + obj;
                }
                return krStr;
            }
            if (typeof obj === 'object') {
                Object.keys(obj).forEach(key => {
                    const value = obj[key];
                    return processKrOrdnung(value);
                });
            }
        };
        processKrOrdnung(kr_ordnung);
        kr_ordnung = krStr;
    }

    if (geografischer_bezug || hasStichworte) {
        const detailsListEl = detailsEl.querySelector('.aps-document_details-items');

        if (geografischer_bezug) createDetailsItem(detailsListEl, 'geografischer_bezug', geografischer_bezug);
        if (hasStichworte) {
            const listRowEl = document.createElement('div');
            listRowEl.classList.add('aps-document_details-item');
            listRowEl.innerHTML = `
                <span class="aps-document_details-item_title">${keyMapping['stichworte']}:&nbsp;</span>
                <span class="aps-document_details-item_tags tag-group"></span>
            `;
            detailsListEl.appendChild(listRowEl);
            tags.map(tag => createDetailsItemTag(listRowEl, tag));
        }
    } else {
        detailsEl.remove();
    }
    if (params.dataNumber || archive || objektinformationen) {
        const objectListEl = objectEl.querySelector('.aps-document_details-items');

        if (params.dataNumber) createDetailsItem(objectListEl, 'docNumber', params.dataNumber);
        if (archive) createDetailsItem(objectListEl, 'archive', archive);
        if (objektinformationen) {
            Object.keys(objektinformationen).forEach(key => {
                const value = objektinformationen[key];
                createDetailsItem(objectListEl, key, value);
            });
        }
    } else {
        objectEl.remove();
    }
    if (kr_ordnung || copyright || publikation) {
        const additionalListEl = additionalEl.querySelector('.aps-document_details-items');

        if (kr_ordnung) createDetailsItem(additionalListEl, 'kr_ordnung', kr_ordnung);
        if (copyright) createDetailsItem(additionalListEl, 'copyright', copyright);
        if (publikation) createDetailsItem(additionalListEl, 'publikation', publikation);
    } else {
        additionalEl.remove();
    }
}
async function initSimiliarImages(params, selected) {
    await fetchApsData(`/api/document/${selected}/similar`)
        .then(similiarImagesResponse => {
            const loadingEl = docEl.querySelector('.aps-document_similiar-attachments .sk-load-box-row');
            if (similiarImagesResponse.error !== true) {
                const similiarImages = similiarImagesResponse.data.getSimilarAttachments;
                const carouselEl = docEl.querySelector('.aps-document_similiar-attachments .topic-carousel');
                const carouselInnerEl = carouselEl.querySelector('.scroll-snap-slider');

                const carouselErrorEl = carouselEl.querySelector('.aps-fetch-error');
                const carouselNoResultsEl = carouselEl.querySelector('.aps-fetch-no-items');
                if (carouselErrorEl) carouselErrorEl.remove();
                if (carouselNoResultsEl) carouselNoResultsEl.remove();

                if (similiarImages && similiarImages.length > 0) {
                    insertAttachments(carouselInnerEl, similiarImages, params);
                    carouselEl.classList.add('loaded');
                    initSnapSlider(carouselEl);
                } else if (similiarImages.length === 0) {
                    carouselEl.remove();

                    const carouselNoResultsEl = document.createElement('div');
                    carouselNoResultsEl.classList.add('aps-fetch-no-items');
                    carouselNoResultsEl.innerHTML = `Es wurden keine keine ähnlichen Motive gefunden.`;
                    docEl.querySelector('.aps-document_similiar-attachments').appendChild(carouselNoResultsEl);
                }
            } else {
                const carouselErrorEl = document.createElement('div');
                carouselErrorEl.classList.add('aps-fetch-error');
                carouselErrorEl.innerHTML = `
                        Es tut uns leid, aber die Abfrage ähnlicher Motive ist fehlgeschlagen.
                        <button type="button" title="Erneut versuchen">
                            Erneut versuchen.
                        </button>
                    `;
                docEl.querySelector('.aps-document_similiar-attachments').appendChild(carouselErrorEl);
                carouselErrorEl.querySelector('button').addEventListener('click', () => {
                    carouselErrorEl.remove();
                    initSimiliarImages(params, selected);
                });
            }
            if (loadingEl) loadingEl.remove();
        });
}
async function initDocumentNotes(params) {
    await fetchApsData(`/api/note/${params.dataNumber}/${params.dataAttachment}`)
        .then(noteResponse => {
            if(noteResponse.status !== 404 && noteResponse.message !== ''){
                const noteEl = docEl.querySelector('.aps-document_notices');
                const textareaEl = noteEl.querySelector('textarea');
                textareaEl.value = noteResponse.message;
                noteLength = noteResponse.message.length;
            } else {
                noteLength = 0;
            }
        });
}
function initDocumentOrderForm() {
    if(docEl.querySelector('.aps-order-form-modal')) {
        const form = docEl.querySelector('.aps-order-form-modal .needs-validation');
        form.addEventListener('submit', async e => {
            e.preventDefault();
            e.stopPropagation();
            if (!form.checkValidity()) {
                console.error('Order form is not valid: aborting submit');
            } else {
                const formData = new FormData(e.target);
                const data = Object.fromEntries(formData.entries());
                const orderSubmitResponse = await fetchApsData(`/api/document/${documentParams.dataAttachment}/order`, 'POST', data);
                if(Array.isArray(orderSubmitResponse) && orderSubmitResponse.length === 0) {
                    setAlert('success', 'Ihre Bestellung wurde erfolgreich versandt. Wir kümmern uns zeitnah darum!');
                    form.reset();
                    docEl.querySelector('.aps-order-form-modal .btn-close').click();
                } else {
                    setAlert('error', 'Ihre Bestellung konnte nicht versandt werden. Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.');
                }
            }

            form.classList.add('was-validated');
        }, false);
    }
}
function insertAttachments(parentEl, data, params, detailPage = false) {
    data.forEach((slide, index) => {
        const thumbnail = slide?.urls?.thumbnail || slide?.thumbnail;
        if (thumbnail !== null) {
            let url;
            if (detailPage) {
                url = {
                    pathname: `/dokument/${params.dataNumber}/${slide.id}`
                }
            } else {
                url = {
                    pathname: '/suche',
                    query: {
                        q: slide.id || '*',
                    },
                };
            }

            const selected = slide.id === params.dataAttachment ? '<div class="topic-carousel_item_selected">Ausgewählt</div>' : '';

            const slideEl = document.createElement('div');
            slideEl.classList.add('scroll-snap-slide');
            slideEl.dataset.index = index;
            slideEl.innerHTML = `
                <a href="${formatUrl(url)}" title="Medium '${slide.id}'">
                    <div class="topic-carousel_item_img-wrap">
                        <div class="topic-carousel_item_image" style="background-image: url(${thumbnail});">
                            <img src="${thumbnail}" class="visually-hidden">
                        </div>
                        <div data-overlay="true" class="topic-carousel_item_overlay"></div>
                        ${selected}
                    </div>
                </a>
            `;
            parentEl.appendChild(slideEl);
        }
    });
}
function createDetailsItem(parent, key, value) {
    const title = keyMapping[key] || key;
    const itemEl = document.createElement('div');
    itemEl.classList.add('aps-document_details-item');
    itemEl.innerHTML = `
        <span class="aps-document_details-item_title">${title}:&nbsp;</span>
        <span class="aps-document_details-item_value">${value}</span>
    `;
    parent.appendChild(itemEl);
}
function createDetailsItemTag(parent, tag) {
    const {facet, IconComp} = typeMap[tag.type] || {};
    const hasIcon = IconComp != null;
    const href = {
        pathname: getUrl('search'),
        query: {
            ...(facet ? {q: '*', requiredfields: serializeRF({[facet]: [tag.value]})} : {q: tag.value})
        },
    };

    const tagEl = document.createElement('a');
    tagEl.classList.add('tag', ...(hasIcon ? ['has-icon'] : []));
    tagEl.href = formatUrl(href);
    tagEl.title = `Suche nach ${tag.value}`;
    tagEl.innerHTML = tag.value;
    parent.querySelector('.aps-document_details-item_tags').appendChild(tagEl);

    if (hasIcon && IconComp !== '') {
        const iconEl = document.createElement('span');
        iconEl.classList.add('tag_icon');
        iconEl.innerHTML = `<svg focusable="false" viewBox="0 0 24 24" aria-hidden="true">${IconComp}</svg>`;
        tagEl.prepend(iconEl);
    }
}